import {faSquare} from '@fortawesome/pro-regular-svg-icons/faSquare';
import {faSquareCheck} from '@fortawesome/pro-regular-svg-icons/faSquareCheck';
import {faSquareMinus} from '@fortawesome/pro-regular-svg-icons/faSquareMinus';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import {AutocompleteProps} from '@mui/material/Autocomplete/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import {ChipProps} from '@mui/material/Chip/Chip';
import {styled} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import {useTranslation} from 'react-i18next';

const icon = <FontAwesomeIcon size="lg" icon={faSquare} />;
const checkedIcon = <FontAwesomeIcon size="lg" icon={faSquareCheck} />;
const indeterminateIcon = <FontAwesomeIcon size="lg" icon={faSquareMinus} />;

export type Option = {label: string; id: string | number};

type AutoCompleteSelectAll = AutocompleteProps<Option, true, false, false>;

export type AutocompleteSelectAllProps = {
  value: Option[];
  options: Option[];
  onChange: (options: Option[]) => void;
  renderInput?: AutoCompleteSelectAll['renderInput'];
} & Omit<
  AutoCompleteSelectAll,
  | 'multiple'
  | 'disableCloseOnSelect'
  | 'value'
  | 'options'
  | 'onChange'
  | 'renderInput'
  | 'renderOption'
  | 'renderTags'
  | 'isOptionEqualToValue'
  | 'filterOptions'
  | 'getOptionLabel'
>;

const selectAllName = 'all';

export const AutocompleteSelectAll = ({options, value, onChange, ...props}: AutocompleteSelectAllProps) => {
  const {t} = useTranslation();
  const filter = createFilterOptions<Option>();

  const {renderInput, ...rest} = props;

  return (
    <Autocomplete
      {...rest}
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={renderInput ?? ((params) => <TextField {...params} />)}
      onChange={(_, selectedOptions, reason, details) => {
        if (reason === 'clear') {
          onChange(selectedOptions);
          return;
        }

        if (details?.option.id === selectAllName) {
          if (selectedOptions.filter((option) => option.id !== selectAllName).length === options.length) {
            onChange([]);
            return;
          }
          onChange(options);
          return;
        }

        onChange(selectedOptions);
      }}
      renderOption={({key, ...optionProps}, option, {selected}) => {
        if (option.id === selectAllName) {
          const isChecked = value.length === options.length;
          const isIndeterminate = value.length > 0 && value.length < options.length;
          return (
            <li key={key} {...optionProps}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                indeterminateIcon={indeterminateIcon}
                checked={isChecked}
                indeterminate={isIndeterminate}
              />
              {!isChecked || isIndeterminate ? t('labels.select_all') : t('labels.select_none')}
            </li>
          );
        }
        return (
          <li key={key} {...optionProps}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
            {option.label}
          </li>
        );
      }}
      renderTags={(values, getTagProps, ownerState) => {
        const numTags = values.length;
        const {limitTags} = ownerState;
        return (
          <>
            {values.slice(0, limitTags).map((option, index) => (
              <ChipSelect {...getTagProps({index})} key={option.id} label={option.label} />
            ))}

            {numTags > limitTags! && <ChipSelect label={`+${numTags - limitTags!}`} />}
          </>
        );
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params) as Option[];
        return [{label: selectAllName, id: selectAllName} as Option, ...filtered];
      }}
    />
  );
};

const ChipSelect = styled((props: ChipProps) => <Chip color="primary" size="small" variant="soft" {...props} />)();
